import cx from 'classnames'
import pluralize from 'pluralize'
import { useRef, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { MdDelete, MdVisibility, MdVisibilityOff } from 'react-icons/md'

import { useClickOutside } from '../../hooks'
import './main.scss'
import { RenderWarning } from '../igao/components/warnings'
import { Warning } from '../igao/components/warnings/warning'
import { findById, headerType, staticType, uid } from '../igao/utils'
import { Divider } from '../shared/Divider'

import QuestionnaireTable from './components/questionnaire-table'
import RenderItem from './components/render-item'
import StaticTable from './components/static-table'
import { getNewItem, onTemplateChange } from './controller'
import styles from './index.module.scss'

export const TableHead = ({ title, type = 'default', style = {}, onClick }) => (
  <button
    className={cx(styles.head, styles[type])}
    style={style}
    onClick={onClick}
  >
    <div>{title}</div>
  </button>
)

const TemplateGenerator = ({ data, setPageData, page, setModal }) => {
  const [ editableQuestion, setEditableQuestion ] = useState(null)
  const [ tab, setTab ] = useState(null)
  const [ editHead, setEditHead ] = useState({
    value: '',
    open: false
  })
  const [ newItem, setNewItem ] = useState({
    id: uid(),
    page,
    igao: true
  })

  const tempData = { ...data }

  const applyChanges = editedQuestion => {
    setPageData(prevData => {
      const _prevData = { ...prevData }
      const item = findById(_prevData[page].items, editedQuestion.id)
      const itemIndex = _prevData[page].items.findIndex(
        i => i.id === editedQuestion.id
      )
      if (!staticType.includes(editedQuestion.type)) {
        _prevData[page].items[itemIndex] = editedQuestion
        _prevData[page].items[itemIndex].type =
          editedQuestion?.relations?.length > 0 ? 'dynamic' : 'static'
        const template = _prevData[page].items[itemIndex].template
          .replaceAll('{', "<span style='font-weight: bold;'>%%")
          .replaceAll('}', '%%</span>')
        if (template !== _prevData[page].items[itemIndex].template) {
          _prevData[page].items[itemIndex].template = template
          _prevData[page].items[itemIndex].issues = []
          _prevData[page].items[itemIndex].relations.map(r => {
            const _r = { ...r }
            _r.values = []
            return _r
          })
          setEditableQuestion(null)
          return _prevData
        }
      } else if (
        headerType.includes(editedQuestion.type) ||
        editedQuestion.type === 'other'
      ) {
        item.label = editedQuestion.label
      } else {
        _prevData[page].items[itemIndex] = {
          id: editedQuestion.id,
          label: editedQuestion.label,
          response: null,
          type: editedQuestion.type
        }
      }
      return _prevData
    })
    setEditableQuestion(null)
  }

  const saveTitle = () => {
    if (editHead.value.length > 0) {
      tempData[page].title = editHead.value
      setEditHead({
        open: false,
        value: ''
      })
      setPageData(tempData)
    }
  }

  const setEditable = question => {
    setEditableQuestion(prev => (question.id === prev?.id ? null : question))
  }

  const handleKeyPress = e => {
    if (e.keyCode === 13 || e.charCode === 13) {
      e.preventDefault()
      saveTitle()
    }
  }

  const handleChangeTab = type => {
    if (tab === type) {
      setTab(null)
    } else {
      setTab(type)
    }
  }

  const saveNewItem = question => {
    const item = getNewItem(question, data[page].lists)
    tempData[page].items.push(item)
    setNewItem({
      id: uid(),
      page,
      igao: true
    })
    setPageData(tempData)
  }

  const handleOnKeyDown = (e, ref, _newItem, edit = true) => {
    const item = onTemplateChange(e, ref, _newItem, data[page].lists)
    if (!edit) setNewItem(item)
    else setEditableQuestion(item)
  }

  const handleHide = id => {
    setPageData(prev => {
      const _data = { ...prev }
      const item = _data[page].items.find(i => i.id === id)
      item.hidden = !item.hidden
      return _data
    })
  }

  const saveStatic = item => {
    tempData[page].items.push({
      ...item,
      type: tab,
      draft: true
    })
    setPageData(tempData)
  }

  const deleteItem = question => {
    setPageData(prev => {
      const _data = { ...prev }
      _data[page].items = prev[page].items.filter(i => i.id !== question.id)
      return _data
    })
  }

  const Question = ({ question, index }) => {
    const questionRef = useRef()
    useClickOutside(questionRef, () => {
      setEditableQuestion(null)
    })

    const actionIcon = () => {
      if (question.draft || headerType.includes(question?.type)) {
        return (
          <button
            className={styles.hideButton}
            onClick={e => {
              e.stopPropagation()
              deleteItem(question)
            }}
          >
            <MdDelete size='1.3rem' />
          </button>
        )
      }
      return (
        <button
          className={cx(styles.hideButton, {
            [styles.modified]: Object.prototype.hasOwnProperty.call(question?.diff ?? {}, 'hidden')
          })}
          onClick={e => {
            e.stopPropagation()
            handleHide(question.id)
          }}
        >
          {question?.hidden ? <MdVisibilityOff size='1.3rem' /> : <MdVisibility size='1.3rem' /> }
        </button>
      )
    }

    return (
      <Draggable
        draggableId={question.id}
        index={index}
      >
        {provided => (
          <RenderItem
            provided={provided}
            question={question}
            items={tempData[page].items}
            applyChanges={applyChanges}
            icon={actionIcon}
            setEditable={setEditable}
            editableQuestion={editableQuestion}
            page={page}
            lists={tempData[page].lists}
            setEditableQuestion={setEditableQuestion}
            handleOnKeyDown={handleOnKeyDown}
            grid={data.length}
          />
        )}
      </Draggable>
    )
  }

  const QuestionList = ({ list }) => {
    return (
      list.map((question, index) => (
        <Question
          question={question}
          key={question.id}
          index={index}
        />
      )) || <div />
    )
  }

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [ removed ] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  const onDragEnd = result => {
    setPageData(prev => {
      const _data = { ...prev }
      if (!result.destination) {
        return _data
      }

      if (editableQuestion) {
        return _data
      }

      if (result.destination.index === result.source.index) {
        return _data
      }

      _data[page].items = reorder(
        data?.[page]?.items ?? [],
        result.source.index,
        result.destination.index
      )

      return _data
    })
  }

  return (
    <div className={styles.igaoRoot}>
      <div className={styles.body}>
        <div className={styles.questions}>
          {!editHead.open
            ? (
              <TableHead
                style={{ fontSize: '19px' }}
                title={data?.[page]?.title}
                onClick={() =>
                  setEditHead(prev => ({
                    ...prev,
                    open: true,
                    value: data?.[page]?.title
                  }))}
              />
            )
            : (
              <input
                value={editHead.value || ''}
                type='text'
                onKeyPress={e => handleKeyPress(e)}
                style={{ marginBottom: '12px' }}
                className={cx(styles.input, styles.comment)}
                placeholder='Title'
                onChange={e =>
                  setEditHead(prev => ({
                    ...prev,
                    value: e.target.value
                  }))}
              />
            )}

          <div className={styles.headerButtons}>
            <BodyHead
              title='Add Question'
              onClick={() => handleChangeTab('question')}
            />
            <BodyHead
              color='orange'
              title='Add Header'
              onClick={() => handleChangeTab('header')}
            />
            <BodyHead
              color='orange'
              title='Add Label'
              onClick={() => handleChangeTab('label')}
            />
          </div>
        </div>
        {tab === 'question' && (
          <QuestionnaireTable
            setTab={setTab}
            data={data}
            newItem={newItem}
            saveNewItem={saveNewItem}
            setNewItem={setNewItem}
            page={page}
            handleOnKeyDown={handleOnKeyDown}
            setModal={setModal}
          />
        )}
        {tab === 'header' && (
          <StaticTable
            tab={tab}
            setTab={setTab}
            saveStatic={saveStatic}
            items={tempData[page].items}
            setModal={setModal}
          />
        )}
        {tab === 'label' && (
          <StaticTable
            tab={tab}
            setTab={setTab}
            saveStatic={saveStatic}
            items={tempData[page].items}
            setModal={setModal}
          />
        )}

        <Divider />
        {tempData[page].items.length > 8 && (
          <RenderWarning
            pageData={tempData[page]}
            withMargin
          />
        )}

        <div className={styles.draggableTable}>
          <DragDropContext onDragEnd={r => onDragEnd(r)}>
            <Droppable droppableId='all-columns'>
              {provided => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  <QuestionList list={data?.[page]?.items} />
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
        <RenderWarning
          pageData={tempData[page]}
          withMargin
        />
        {tempData[page].diff?.items && (
          <Warning
            type='concurrent'
            title={`In the current draft, you deleted ${
              tempData[page].diff?.items.length
            } ${pluralize('item', tempData[page].diff?.items.length)}`}
            withMargin
          />
        )}
      </div>
    </div>
  )
}

function BodyHead (props) {
  return (
    <button
      className={cx(styles.button, [ props.color === 'orange' && styles.orange ])}
      {...props}
    >
      {props.title}
    </button>
  )
}

export default TemplateGenerator
